import { Capacitor } from '@capacitor/core';

import { isIpad } from 'src/util/platform';

export const desktopPointerInteractionQuery = '(hover: hover) and (pointer: fine)';

// Ab dieser Breite kann der Wizard alle Schritte zeigen. Entspricht dem
// lg-Breakpoint von useBreakpoint().isDesktop und den Desktop-Zweigen in MainLayout.vue.
const DESKTOP_MIN_VIEWPORT_WIDTH = 1024;
const SETUP_WIZARD_MIN_VIEWPORT_WIDTH = DESKTOP_MIN_VIEWPORT_WIDTH;

type BrowserInteractionEnvironment = {
	isNativeCapacitorPlatform: boolean;
	isNativeIpad?: boolean;
	matchMedia: ((query: string) => Pick<MediaQueryList, 'matches'>) | undefined;
};

const resolveBrowserInteractionEnvironment = (): BrowserInteractionEnvironment => {
	const matchMedia =
		typeof window === 'undefined' || typeof window.matchMedia !== 'function'
			? undefined
			: window.matchMedia.bind(window);

	const isNativeCapacitorPlatform = Capacitor.isNativePlatform();

	return {
		isNativeCapacitorPlatform,
		isNativeIpad: isNativeCapacitorPlatform && isIpad(),
		matchMedia,
	};
};

export const isNativeCapacitorPlatform = (): boolean => {
	return Capacitor.isNativePlatform();
};

export const isRunningInNativeCapacitorApp = (): boolean => {
	return isNativeCapacitorPlatform();
};

export const canUseDesktopPointerInteractions = (
	environment: BrowserInteractionEnvironment = resolveBrowserInteractionEnvironment()
): boolean => {
	if (environment.isNativeCapacitorPlatform) {
		return false;
	}

	if (environment.matchMedia === undefined) {
		return false;
	}

	return environment.matchMedia(desktopPointerInteractionQuery).matches;
};

export const canUseDesktopSplitView = (
	isDesktopViewport: boolean,
	environment: BrowserInteractionEnvironment = resolveBrowserInteractionEnvironment()
): boolean => {
	return isDesktopViewport && canUseDesktopPointerInteractions(environment);
};

export const canUseDesktopBrowserFileDropInteractions = (
	environment: BrowserInteractionEnvironment = resolveBrowserInteractionEnvironment()
): boolean => {
	return canUseDesktopPointerInteractions(environment);
};

export const canUseSpotlightSearch = (
	spotlightSearchEnabled: boolean,
	environment: BrowserInteractionEnvironment = resolveBrowserInteractionEnvironment()
): boolean => {
	return spotlightSearchEnabled && !environment.isNativeCapacitorPlatform;
};

const canUseWorkspaceTabsAtDesktopWidth = (
	isDesktopViewport: boolean,
	environment: BrowserInteractionEnvironment
): boolean => {
	return (
		isDesktopViewport &&
		(canUseDesktopPointerInteractions(environment) ||
			(environment.isNativeCapacitorPlatform && environment.isNativeIpad === true))
	);
};

export const canUseWorkspaceTabs = (
	workspaceTabsEnabled: boolean,
	splitViewEnabled: boolean,
	isDesktopViewport: boolean,
	environment: BrowserInteractionEnvironment = resolveBrowserInteractionEnvironment()
): boolean => {
	return (
		workspaceTabsEnabled && splitViewEnabled && canUseWorkspaceTabsAtDesktopWidth(isDesktopViewport, environment)
	);
};

export const canUseWorkspaceTabsSettings = (
	viewportWidth: number,
	environment: BrowserInteractionEnvironment = resolveBrowserInteractionEnvironment()
): boolean => {
	return (
		(canUseDesktopPointerInteractions(environment) && viewportWidth > 600) ||
		(environment.isNativeCapacitorPlatform &&
			environment.isNativeIpad === true &&
			viewportWidth >= DESKTOP_MIN_VIEWPORT_WIDTH)
	);
};

/**
 * Decides whether the setup wizard may open automatically on the current device.
 *
 * The wizard writes user-wide preferences and contains steps that only exist on
 * desktop layouts. Restricting the automatic start keeps the one-shot onboarding
 * for the device that can show every step.
 *
 * @param viewportWidth - Current viewport width in CSS pixels.
 * @param environment - Injectable browser capabilities, defaults to the real browser.
 */
export const canAutoOpenSetupWizard = (
	viewportWidth: number,
	environment: BrowserInteractionEnvironment = resolveBrowserInteractionEnvironment()
): boolean => {
	return canUseDesktopPointerInteractions(environment) && viewportWidth >= SETUP_WIZARD_MIN_VIEWPORT_WIDTH;
};
